/**
 * Payment-intent detail.
 *
 * Hero card → tabs (Overview / Timeline / Provider / Raw). The hero
 * carries a "Query status" action on non-terminal intents (PR-7b /
 * spec 0004 §5.1.1) that forces an upstream status refresh — the
 * operator's escape hatch for a stuck dispatch.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RotateCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { formatMethod } from "@/lib/format";
import { DetailHero } from "@/components/payments/DetailHero";
import { JsonBlock } from "@/components/payments/JsonBlock";
import { Timeline } from "@/components/payments/Timeline";
import { ActionConfirmDialog } from "@/components/observability/ActionConfirmDialog";
import {
  asApiError,
  getPaymentIntent,
  syncPaymentIntentStatus,
  type IntentState,
  type PaymentIntentDetail,
  type SyncPaymentIntentStatusResponse,
} from "@/lib/api";
import { INTENT_STATE_BADGE_VARIANT, PROVIDER_TX_STATUS_VARIANT } from "@/lib/state-badges";

// States where the "Query status" button is hidden. Distinct from
// the backend's state-machine ``TERMINAL_STATES``
// (``{failed, cancelled, refunded}``) — those forbid further
// transitions; this is a SPA-side superset that ALSO hides the
// button on ``succeeded`` (PR-7b review #1).
//
// Why: ``succeeded → refunding`` is a legal transition (the
// backend's ``noop_terminal`` pre-flight correctly doesn't catch
// ``succeeded``), so a click would dispatch a real upstream query
// → upstream replies ``succeeded`` → dispatcher hits
// ``can_transition("succeeded", "succeeded") == False`` → 409
// ``incompatible_state``. Every click costs a real Daraja/SasaPay
// charge for a guaranteed-409 toast. Refund flow is a separate
// surface (not yet wired); it's not the path operators take from
// here.
//
// ``refunding`` STAYS sync-able: ``can_transition("refunding",
// "succeeded")`` IS true (refunding can roll back to succeeded if
// the refund fails upstream), so a click is operationally useful.
const NO_SYNC_STATES: ReadonlySet<IntentState> = new Set([
  "failed",
  "cancelled",
  "refunded",
  "succeeded",
] as const);

export function PaymentIntentDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["payment-intent", id] as const,
    queryFn: () => getPaymentIntent(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load this payment intent", {
        description: "It may not exist or you may not have access.",
      });
    }
  }, [query.error]);

  if (query.isLoading) {
    return <DetailSkeleton />;
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this payment intent</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              It may not exist or you may not have access.{" "}
              <Link to="/payment-intents" className="text-foreground underline">
                Back to list
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }
  return <Loaded intent={query.data} />;
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-9 w-72" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function Loaded({ intent }: { intent: PaymentIntentDetail }) {
  const queryClient = useQueryClient();
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const canSync = !NO_SYNC_STATES.has(intent.state);

  const syncMutation = useMutation({
    mutationFn: (vars: { reason: string }) =>
      syncPaymentIntentStatus(intent.id, { reason: vars.reason }),
    onSuccess: (data) => {
      // Outcome-aware toast — different outcomes warrant different
      // operator-facing messages.
      announceSyncOutcome(data);
      setSyncOpen(false);
      // Refresh the page even on noop outcomes — cheap, and a
      // sync_settled outcome wrote a new event the Timeline tab
      // should render.
      void queryClient.invalidateQueries({ queryKey: ["payment-intent", intent.id] });
    },
    onError: (err: unknown) => {
      const body = asApiError(err);
      setSyncError(body?.detail ?? "Couldn't query upstream status.");
    },
  });

  return (
    <>
      <DetailHero
        backHref="/payment-intents"
        backLabel="Back to payment intents"
        id={intent.id}
        amount={intent.amount}
        currency={intent.currency}
        stateLabel={intent.state}
        stateVariant={INTENT_STATE_BADGE_VARIANT[intent.state]}
        meta={
          <>
            <span className="font-medium">{intent.tenant_name}</span>
            <Badge variant="muted" className="capitalize">
              {intent.tenant_environment}
            </Badge>
            <span>·</span>
            <span className="capitalize">{intent.direction}</span>
            <span>·</span>
            <span>{formatMethod(intent.method)}</span>
            <span>·</span>
            <span title={intent.created_at}>
              created {new Date(intent.created_at).toLocaleString()}
            </span>
          </>
        }
        actions={
          canSync ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSyncError(null);
                setSyncOpen(true);
              }}
            >
              <RotateCw />
              Query status
            </Button>
          ) : undefined
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">
            Timeline
            {intent.events.length > 0 ? (
              <span className="ml-1.5 text-xs text-muted-foreground">{intent.events.length}</span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="provider">Provider</TabsTrigger>
          <TabsTrigger value="raw">Raw</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Intent ID" mono>
                    {intent.id}
                  </Def>
                  <Def label="Idempotency key" mono>
                    {intent.idempotency_key || "—"}
                  </Def>
                  <Def label="Direction">
                    <span className="capitalize">{intent.direction}</span>
                  </Def>
                  <Def label="Method">{formatMethod(intent.method)}</Def>
                  {intent.refund_of_id ? (
                    <Def label="Refund of">
                      <Link
                        to={`/payment-intents/${intent.refund_of_id}`}
                        className="font-mono text-xs underline"
                      >
                        {intent.refund_of_id}
                      </Link>
                    </Def>
                  ) : null}
                </Defs>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tenant + timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Tenant">{intent.tenant_name}</Def>
                  <Def label="Environment">
                    <Badge variant="muted" className="capitalize">
                      {intent.tenant_environment}
                    </Badge>
                  </Def>
                  <Def label="Created">{new Date(intent.created_at).toLocaleString()}</Def>
                  <Def label="Updated">{new Date(intent.updated_at).toLocaleString()}</Def>
                </Defs>
              </CardContent>
            </Card>
          </div>

          {(Object.keys(intent.counterparty).length > 0 ||
            Object.keys(intent.metadata).length > 0) && (
            <div className="grid gap-4 lg:grid-cols-2">
              {Object.keys(intent.counterparty).length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Counterparty</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <JsonBlock value={intent.counterparty} />
                  </CardContent>
                </Card>
              ) : null}
              {Object.keys(intent.metadata).length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Metadata</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <JsonBlock value={intent.metadata} />
                  </CardContent>
                </Card>
              ) : null}
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Event log</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline events={intent.events} truncated={intent.events_truncated} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="provider">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Provider transaction</CardTitle>
            </CardHeader>
            <CardContent>
              {intent.provider_transaction ? (
                <Defs>
                  <Def label="Status">
                    <Badge
                      variant={PROVIDER_TX_STATUS_VARIANT[intent.provider_transaction.status]}
                      className="capitalize"
                    >
                      {intent.provider_transaction.status}
                    </Badge>
                  </Def>
                  <Def label="Plugin">{intent.provider_transaction.plugin}</Def>
                  <Def label="Provider ref" mono>
                    {intent.provider_transaction.provider_ref}
                  </Def>
                  {intent.provider_transaction.error_code ? (
                    <Def label="Error code">
                      <span className="font-mono text-xs text-destructive">
                        {intent.provider_transaction.error_code}
                      </span>
                    </Def>
                  ) : null}
                  {intent.provider_transaction.provider_error_code ? (
                    <Def label="Provider error code">
                      <span className="font-mono text-xs text-destructive">
                        {intent.provider_transaction.provider_error_code}
                      </span>
                    </Def>
                  ) : null}
                  {intent.provider_transaction.reconciled_at ? (
                    <Def label="Reconciled">
                      {new Date(intent.provider_transaction.reconciled_at).toLocaleString()}
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        via {intent.provider_transaction.reconciliation_source ?? "unknown"}
                      </span>
                    </Def>
                  ) : null}
                </Defs>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No provider transaction yet — the intent never reached dispatch.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="raw" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Request snapshot</CardTitle>
              <p className="text-xs text-muted-foreground">
                The original payload as received by the public API.
              </p>
            </CardHeader>
            <CardContent>
              <JsonBlock value={intent.request_snapshot} />
            </CardContent>
          </Card>

          {intent.response_snapshot ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Response snapshot</CardTitle>
                <p className="text-xs text-muted-foreground">
                  What the API returned to the caller.
                </p>
              </CardHeader>
              <CardContent>
                <JsonBlock value={intent.response_snapshot} />
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>
      </Tabs>

      <ActionConfirmDialog
        open={syncOpen}
        onOpenChange={(open) => {
          setSyncOpen(open);
          if (!open) setSyncError(null);
        }}
        title="Query upstream status"
        description={
          "Forces an immediate status refresh against the upstream provider. " +
          "Useful when a callback was dropped and the intent is stuck in processing. " +
          "A reason is required and is captured in the admin audit log."
        }
        confirmLabel="Query"
        pending={syncMutation.isPending}
        error={syncError}
        onSubmit={({ reason }) => syncMutation.mutate({ reason })}
      />
    </>
  );
}


/** Map a :class:`StatusQueryOutcome` to an operator-facing toast.
 *
 * Each documented outcome (spec 0004 §5.1.1) has a distinct
 * message: "sync_settled" tells the operator the intent moved; the
 * various 4xx outcomes explain why nothing happened so they know
 * what to do next. 5xx outcomes get a destructive toast so the
 * incident is visible.
 */
function announceSyncOutcome(data: SyncPaymentIntentStatusResponse): void {
  const { outcome, state, result_desc } = data;
  switch (outcome) {
    case "sync_settled":
      toast.success(`Intent transitioned to ${state ?? "unknown"}`, {
        description: result_desc ?? "Upstream returned a terminal verdict.",
      });
      return;
    case "noop_terminal":
      toast.info("Intent was already terminal", {
        description: "No upstream query was issued.",
      });
      return;
    case "async_dispatched":
      toast.info("Status query dispatched", {
        description:
          "The provider acknowledged the query; the result will arrive on a callback. Refresh shortly.",
      });
      return;
    case "recorded_pending":
      toast.info("Upstream replied 'not yet processed'", {
        description:
          "The query was recorded but the upstream doesn't know yet. Try again in a minute.",
      });
      return;
    case "dispatch_in_flight":
      toast.warning("Another status query is already in flight", {
        description:
          "Wait for the prior query to settle (or its result to land) before retrying.",
      });
      return;
    case "missing_provider_tx":
      toast.warning("Intent has no provider dispatch to query", {
        description:
          "This intent never reached an upstream — nothing to query. Likely a routing failure at creation time.",
      });
      return;
    case "incompatible_state":
      toast.warning("Upstream verdict incompatible with intent state", {
        description:
          result_desc ??
          "The provider's view of this transaction can't be reconciled with the intent's current state.",
      });
      return;
    case "unsupported":
      toast.warning("Status queries aren't on for this payment", {
        description:
          "They're off for this provider or account, so nothing was sent. The payment stays as it is.",
      });
      return;
    case "account_inactive":
      toast.warning("The account that sent this payment is disabled", {
        description: "Nothing was sent. Results only reach active accounts.",
      });
      return;
    case "credential_blocked":
    case "credential_budget_exhausted":
    case "credential_unproven_in_flight":
      toast.warning("The credential guard refused this query", {
        description: `Nothing was sent (${outcome}). Check the account's credential.`,
      });
      return;
    case "upstream_failed":
      toast.error("Upstream provider rejected the query", {
        description: result_desc ?? "Check provider status and retry.",
      });
      return;
    case "routing_failed":
      toast.error("Routing failed", {
        description:
          "No active provider route for this intent's method. Check the tenant's provider accounts.",
      });
      return;
    case "plugin_unavailable":
      // Distinct from routing_failed: the route lookup found a
      // ProviderAccount with a plugin name, but the registry
      // doesn't have an instance for it. Usually means a plugin
      // wasn't installed in the running container or its
      // entry-point load failed at startup.
      toast.error("Provider plugin not loaded", {
        description:
          "The route's plugin isn't installed in this deployment. Check the plugin install + worker startup logs.",
      });
      return;
    default:
      // Defensive: a new outcome added server-side without an SPA
      // update lands here. Surface it as an info toast so the
      // operator at least sees the raw discriminator.
      toast.info(`Status query returned '${outcome as string}'`);
  }
}

function Defs({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-3">{children}</dl>;
}

function Def({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd
        className={
          mono
            ? "min-w-0 break-all font-mono text-xs sm:text-right"
            : "min-w-0 text-sm sm:text-right"
        }
      >
        {children}
      </dd>
    </div>
  );
}

export default PaymentIntentDetailPage;
